import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import DataTable from "../../components/common/DataTable";
import Input from "../../components/common/Input";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import lotService from "../../services/lotService";
import reportService from "../../services/reportService";
import { formatDate } from "../../utils/dateUtils";

function formatNumber(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(number);
}

function formatWeight(value) {
  return `${formatNumber(value)} kg`;
}

function formatCurrency(value) {
  return `₹ ${formatNumber(value)}`;
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function LotWiseReportPage() {
  const navigate = useNavigate();
  const { lotId } = useEncryptedParams();
  const [selectedLotId, setSelectedLotId] = useState(lotId || "");
  const [activeLots, setActiveLots] = useState([]);
  const [lotReportData, setLotReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLotsLoading, setIsLotsLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedSections, setExpandedSections] = useState({
    bl: false,
    purchase: false,
    lotStock: true,
    stock: true,
    processing: true,
    selling: true,
    expense: true,
    transfer: true,
  });

  useEffect(() => {
    setSelectedLotId(lotId || "");
  }, [lotId]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const loadActiveLots = useCallback(async () => {
    setIsLotsLoading(true);
    try {
      const data = await lotService.getActiveLots({ showLocked: true });
      const items = data.items || [];
      setActiveLots(items);

      // If no lotId in URL and we have lots, select the first one by default
      if (!lotId && items.length > 0) {
        const firstLotId = String(items[0].lotId || items[0].id);
        setSelectedLotId(firstLotId);
        navigate(`/reports/lot-wise/${encrypt(firstLotId)}`, { replace: true });
      }
    } catch (err) {
      setError(err?.message || "Failed to load active lots.");
    } finally {
      setIsLotsLoading(false);
    }
  }, [lotId, navigate]);

  const loadReport = useCallback(async (currentLotId) => {
    if (!currentLotId) {
      setLotReportData([]);
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const report = await reportService.getLotWiseReport(currentLotId);
      setLotReportData(report.items || []);
    } catch (err) {
      setLotReportData([]);
      setError(err?.message || "Failed to load Lot-wise report.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActiveLots();
  }, [loadActiveLots]);

  useEffect(() => {
    loadReport(lotId);
  }, [lotId, loadReport]);

  const lotOptions = useMemo(() => {
    return activeLots.map((lot) => ({
      value: String(lot.lotId || lot.id),
      label: lot.lotNo || `Lot ${lot.lotId || lot.id}`,
    }));
  }, [activeLots]);

  // Flattened data for the tables
  const reportData = useMemo(() => {
    const blRows = [];
    const purchaseMap = new Map();
    const sellingRows = [];
    const expenseRows = [];
    const stockDetails = [];
    const processingHistory = [];
    const lotStockRows = [];
    const transferRows = [];

    lotReportData.forEach((lot) => {
      // Collect BL Details
      if (lot.blDetails && Array.isArray(lot.blDetails)) {
        lot.blDetails.forEach((bl) => blRows.push({ ...bl, lotNo: lot.lotNo }));
      }

      // Collect Purchase Details (Deduplicated)
      if (lot.purchaseDetails && Array.isArray(lot.purchaseDetails)) {
        lot.purchaseDetails.forEach((pd) => {
          // Use a composite key for deduplication to handle redundant rows from API
          const key = `${pd.billNo}-${pd.containerNo}-${pd.stockCategoryName}-${pd.netWeight}-${pd.price}`;
          if (!purchaseMap.has(key)) {
            purchaseMap.set(key, { ...pd, lotNo: lot.lotNo });
          }
        });
      }

      // Collect Available Stock in Lot
      if (lot.lotWiseStock && Array.isArray(lot.lotWiseStock)) {
        lotStockRows.push(...lot.lotWiseStock);
      }

      // Collect Stock & Processing
      if (lot.stockAndProcessing) {
        if (lot.stockAndProcessing.stockDetails) {
          stockDetails.push(...lot.stockAndProcessing.stockDetails);
        }
        if (lot.stockAndProcessing.processingHistory) {
          processingHistory.push(...lot.stockAndProcessing.processingHistory);
        }
      }

      // Collect Selling Details
      if (lot.sellingDetails && Array.isArray(lot.sellingDetails)) {
        lot.sellingDetails.forEach((sd) =>
          sellingRows.push({ ...sd, lotNo: lot.lotNo }),
        );
      }

      // Collect Expense Details
      if (lot.expenseDetails && Array.isArray(lot.expenseDetails)) {
        lot.expenseDetails.forEach((ed) =>
          expenseRows.push({ ...ed, lotNo: lot.lotNo }),
        );
      }

      // Collect Transfer Details
      if (lot.transferDetails && Array.isArray(lot.transferDetails)) {
        lot.transferDetails.forEach((td) =>
          transferRows.push({ ...td, lotNo: lot.lotNo }),
        );
      }
    });

    return {
      blRows,
      purchaseRows: Array.from(purchaseMap.values()),
      stockDetails,
      processingHistory,
      sellingRows,
      expenseRows,
      lotStockRows,
      transferRows,
    };
  }, [lotReportData]);

  const totals = useMemo(() => {
    const res = reportData.purchaseRows.reduce(
      (acc, item) => ({
        netWeight: acc.netWeight + toNumber(item.netWeight),
        totalPrice: acc.totalPrice + toNumber(item.price),
        priceInRS: acc.priceInRS + toNumber(item.priceInRS),
      }),
      { netWeight: 0, totalPrice: 0, priceInRS: 0 },
    );

    const lotSummary = lotReportData[0] || {};

    return {
      ...res,
      currencySymbol: reportData.purchaseRows[0]?.currencySymbol || "$",
      originalWeight: toNumber(lotSummary.originalWeight),
      newOriginalWeight: toNumber(lotSummary.newOriginalWeight),
      totalJointWeight: toNumber(lotSummary.totalJointWeight),
      processedWeight: toNumber(lotSummary.processedWeight),
      remainingWeight: toNumber(lotSummary.remainingWeight),
      lotExcessWeight: toNumber(lotSummary.lotExcessWeight),
      lotWastageWeight: toNumber(lotSummary.lotWastageWeight),
      totalSalesWeight: toNumber(lotSummary.totalSalesWeight),
      totalSalesAmount: toNumber(lotSummary.totalSalesAmount),
      totalGrandTotal: toNumber(
        lotSummary.totalGrandTotal || lotSummary.grandTotal,
      ),
      transferWeight: toNumber(lotSummary.transferWeight),
    };
  }, [reportData.purchaseRows, lotReportData]);

  const blColumns = useMemo(
    () => [
      {
        key: "blNumber",
        label: "BL Number",
        sortable: true,
        render: (item) => (
          <span className="font-bold text-slate-900 dark:text-white">
            {item.blNumber || "-"}
          </span>
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        render: (item) => {
          const status = item.status || "-";
          const colors = {
            Arrived: "text-emerald-600 dark:text-emerald-400",
            Loading: "text-blue-600 dark:text-blue-400",
            "In Transit": "text-purple-600 dark:text-purple-400",
          };
          return (
            <span className={`font-bold ${colors[status] || ""}`}>
              {status}
            </span>
          );
        },
      },
      // {
      //   key: "isConverted",
      //   label: "Converted",
      //   render: (item) => (
      //     <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${item.isConverted ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>
      //       {item.isConverted ? "Yes" : "No"}
      //     </span>
      //   ),
      // },
      {
        key: "dateOfDeparture",
        label: "Departure",
        sortable: true,
        render: (item) => formatDate(item.dateOfDeparture),
      },
      {
        key: "eta",
        label: "ETA",
        sortable: true,
        render: (item) => formatDate(item.eta),
      },
      {
        key: "etaIcd",
        label: "ETA ICD",
        sortable: true,
        render: (item) => formatDate(item.etaIcd),
      },
      {
        key: "freeDays",
        label: "Free Days",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right">{item.freeDays || 0}</span>
        ),
      },
      {
        key: "vendorName",
        label: "Vendor",
        render: (item) => item.vendorName || "-",
      },
      {
        key: "shippingLineName",
        label: "Shipping Line",
        render: (item) => item.shippingLineName || "-",
      },
    ],
    [],
  );

  const purchaseColumns = useMemo(
    () => [
      {
        key: "billNo",
        label: "Bill No",
        sortable: true,
        render: (item) => (
          <span className="font-bold text-slate-900 dark:text-white">
            {item.billNo || "-"}
          </span>
        ),
      },
      { key: "containerNo", label: "Containers", sortable: true },
      {
        key: "stockCategoryName",
        label: "Category",
        render: (item) => item.stockCategoryName || "-",
      },
      {
        key: "netWeight",
        label: "Net Weight (KG)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right font-bold text-primary-600">
            {formatWeight(item.netWeight)}
          </span>
        ),
      },
      {
        key: "price",
        label: `Price`,
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right font-mono">
            {item.currencySymbol || totals.currencySymbol}
            {formatNumber(item.price)}
          </span>
        ),
      },
      {
        key: "dollarRate",
        label: "Currency Rate",
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right">
            {formatNumber(item.dollarRate)}
          </span>
        ),
      },
      {
        key: "priceInRS",
        label: "Price (₹)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right font-bold text-emerald-600 font-mono">
            {formatCurrency(item.priceInRS)}
          </span>
        ),
      },
    ],
    [totals.currencySymbol],
  );

  const lotStockColumns = useMemo(
    () => [
      { key: "stockCategoryName", label: "Category", sortable: true },
      {
        key: "remainingStock",
        label: "Available Stock",
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right font-bold text-primary-600">
            {formatWeight(item.remainingStock)}
          </span>
        ),
      },
    ],
    [],
  );

  const sellingColumns = useMemo(
    () => [
      { key: "invoiceNo", label: "Invoice No", sortable: true },
      { key: "customerName", label: "Customer", sortable: true },
      { key: "date", label: "Date", render: (item) => formatDate(item.date) },
      { key: "stockCategoryName", label: "Category" },
      {
        key: "quantity",
        label: "Qty (KG)",
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right">
            {formatWeight(item.quantity)}
          </span>
        ),
      },
      {
        key: "ratePerKg",
        label: "Rate (₹)",
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right">
            {formatCurrency(item.ratePerKg)}
          </span>
        ),
      },
      {
        key: "materialAmount",
        label: "Material (₹)",
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right">
            {formatCurrency(item.materialAmount)}
          </span>
        ),
      },

      {
        key: "roundOffAmount",
        label: "Round Off (₹)",
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right italic text-slate-500">
            {formatCurrency(item.roundOffAmount)}
          </span>
        ),
      },
      {
        key: "totalAmount",
        label: "Total Amt (₹)",
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right font-bold">
            {formatCurrency(item.totalAmount)}
          </span>
        ),
      },
      {
        key: "grandTotal",
        label: "Grand Total (₹)",
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right font-bold text-emerald-600">
            {formatCurrency(item.grandTotal)}
          </span>
        ),
      },
    ],
    [],
  );

  const expenseColumns = useMemo(
    () => [
      { key: "expenseCategoryName", label: "Expense Category", sortable: true },
      {
        key: "price",
        label: "Price (₹)",
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right">
            {formatCurrency(item.price)}
          </span>
        ),
      },
      { key: "remark", label: "Remark" },
      {
        key: "fileName",
        label: "Attachment",
        render: (item) =>
          item.fileName ? (
            <a
              href={item.fileName}
              target="_blank"
              rel="noreferrer"
              className="text-primary-600 hover:underline"
            >
              View File
            </a>
          ) : (
            "-"
          ),
      },
    ],
    [],
  );

  const transferColumns = useMemo(
    () => [
      {
        key: "createdDate",
        label: "Date",
        sortable: true,
        render: (item) => formatDate(item.createdDate),
      },
      {
        key: "fromLotNo",
        label: "From Lot",
        sortable: true,
        render: (item) => (
          <span className="font-bold text-slate-700 dark:text-slate-200">
            {item.fromLotNo || "-"}
          </span>
        ),
      },
      {
        key: "toLotNo",
        label: "To Lot",
        sortable: true,
        render: (item) => (
          <span className="font-bold text-primary-600">
            {item.toLotNo || "-"}
          </span>
        ),
      },
      {
        key: "fromStockCategoryName",
        label: "Material",
        render: (item) => item.fromStockCategoryName || "-",
      },
      {
        key: "transferWeight",
        label: "Transfer Weight",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right font-bold text-primary-600">
            {formatWeight(item.transferWeight)}
          </span>
        ),
      },
    ],
    [],
  );

  const stockColumns = useMemo(
    () => [
      { key: "stockCategoryName", label: "Category", sortable: true },
      {
        key: "isJoint",
        label: "Is Joint",
        sortable: true,
        render: (item) => (
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${item.isJoint ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}
          >
            {item.isJoint ? "Yes" : "No"}
          </span>
        ),
      },
      {
        key: "weight",
        label: "Weight",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right">
            {formatWeight(item.weight)}
          </span>
        ),
      },
      {
        key: "processedWeight",
        label: "Processed",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right text-emerald-600">
            {formatWeight(item.processedWeight)}
          </span>
        ),
      },
      {
        key: "remainingWeight",
        label: "Remaining",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span
            className={`block w-full text-right font-bold ${item.remainingWeight < 0 ? "text-danger" : "text-primary-700 dark:text-primary-300"}`}
          >
            {formatWeight(item.remainingWeight)}
          </span>
        ),
      },
    ],
    [],
  );

  const historyColumns = useMemo(
    () => [
      {
        key: "date",
        label: "Date",
        sortable: true,
        render: (item) => formatDate(item.date),
      },
      { key: "fromStockCategoryName", label: "Input Category", sortable: true },
      {
        key: "processingWeight",
        label: "Processing Weight",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right">
            {formatWeight(item.processingWeight)}
          </span>
        ),
      },
      {
        key: "outputDetails",
        label: "Output Details",
        render: (item) => (
          <div className="flex flex-wrap gap-1">
            {item.details && item.details.length > 0 ? (
              item.details.map((detail, idx) => (
                <span
                  key={idx}
                  className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                >
                  {detail.stockCategoryName}: {formatWeight(detail.weight)}
                </span>
              ))
            ) : (
              <span className="text-[10px] italic text-slate-400">
                No output details
              </span>
            )}
          </div>
        ),
      },
      {
        key: "labourRate",
        label: "Rate",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right">
            {formatCurrency(item.labourRate)}
          </span>
        ),
      },
      {
        key: "totalLabourCost",
        label: "Total Cost",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full text-right font-bold text-slate-900 dark:text-white">
            {formatCurrency(item.totalLabourCost)}
          </span>
        ),
      },
    ],
    [],
  );

  const handleLotChange = (event) => {
    const value = event.target.value;
    setSelectedLotId(value);
    navigate(
      value ? `/reports/lot-wise/${encrypt(value)}` : "/reports/lot-wise",
    );
  };

  const handleRefresh = () => {
    loadActiveLots();
    loadReport(selectedLotId);
  };

  const selectedLotLabel =
    lotOptions.find((opt) => opt.value === String(selectedLotId))?.label ||
    lotReportData[0]?.lotNo ||
    "";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 dark:border-slate-800 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Reports
          </p>
          <h1 className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
            Lot-wise Report{" "}
            {selectedLotLabel && (
              <span className="text-primary-600 font-black">
                ({selectedLotLabel})
              </span>
            )}
          </h1>
        </div>

        <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto lg:items-end">
          <div className="w-full sm:min-w-[280px]">
            <SearchableDropdown
              label="Lot"
              name="lotId"
              value={selectedLotId}
              onChange={handleLotChange}
              options={lotOptions}
              placeholder={
                isLotsLoading ? "Loading Lots..." : "All active Lots"
              }
              disabled={isLotsLoading}
            />
          </div>
          <Button
            variant="secondary"
            onClick={handleRefresh}
            isLoading={isLoading || isLotsLoading}
            className="w-full sm:w-auto"
          >
            <span className="flex items-center justify-center gap-1.5 text-xs sm:text-sm">
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v6h6M20 20v-6h-6"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M20 9a8 8 0 00-14.9-3M4 15a8 8 0 0014.9 3"
                />
              </svg>
              Refresh
            </span>
          </Button>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm font-medium text-danger">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {/* Original Weight */}
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:text-[11px]">
            Original Lot Weight
          </p>
          <p className="mt-1 text-base font-black text-slate-950 dark:text-white sm:mt-2 sm:text-xl text-right">
            {formatWeight(totals.originalWeight)}
          </p>
        </div>

        {/* New Original Weight */}
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:text-[11px]">
            Updated Lot Weight
          </p>
          <p className="mt-1 text-base font-black text-slate-950 dark:text-white sm:mt-2 sm:text-xl text-right">
            {formatWeight(totals.newOriginalWeight)}
          </p>
        </div>

        {/* Joint Weight */}
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:text-[11px]">
            Joint Weight
          </p>
          <p className="mt-1 text-base font-black text-amber-600 sm:mt-2 sm:text-xl text-right">
            {formatWeight(totals.totalJointWeight)}
          </p>
        </div>

        {/* Processed Weight */}
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:text-[11px]">
            Processed Weight
          </p>
          <p className="mt-1 text-base font-black text-emerald-600 sm:mt-2 sm:text-xl text-right">
            {formatWeight(totals.processedWeight)}
          </p>
        </div>

        {/* Remaining Weight */}
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:text-[11px]">
            Remaining Process Weight
          </p>
          <p className="mt-1 text-base font-black text-primary-600 sm:mt-2 sm:text-xl text-right">
            {formatWeight(totals.remainingWeight)}
          </p>
        </div>

        {/* Transfer Weight */}
        <div className="rounded-xl border border-indigo-100 bg-indigo-50/30 p-3 shadow-sm dark:border-indigo-900/20 dark:bg-indigo-900/5 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-400 sm:text-[11px]">
            Transfer Weight
          </p>
          <p className="mt-1 text-base font-black text-indigo-600 sm:mt-2 sm:text-xl text-right">
            {formatWeight(totals.transferWeight)}
          </p>
        </div>

        {/* Excess Weight */}
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/30 p-3 shadow-sm dark:border-emerald-900/20 dark:bg-emerald-900/5 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 sm:text-[11px]">
            Excess Weight
          </p>
          <p className="mt-1 text-base font-black text-emerald-600 sm:mt-2 sm:text-xl text-right">
            {formatWeight(totals.lotExcessWeight)}
          </p>
        </div>

        {/* Wastage Weight */}
        <div className="rounded-xl border border-rose-100 bg-rose-50/30 p-3 shadow-sm dark:border-rose-900/20 dark:bg-rose-900/5 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400 sm:text-[11px]">
            Wastage Weight
          </p>
          <p className="mt-1 text-base font-black text-rose-600 sm:mt-2 sm:text-xl text-right">
            {formatWeight(totals.lotWastageWeight)}
          </p>
        </div>

        {/* Total Sales Weight */}
        <div className="rounded-xl border border-rose-100 bg-rose-50/30 p-3 shadow-sm dark:border-rose-900/20 dark:bg-rose-900/5 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400 sm:text-[11px]">
            Total Sales Weight
          </p>
          <p className="mt-1 text-base font-black text-right sm:mt-2 sm:text-xl">
            {formatWeight(totals.totalSalesWeight)}
          </p>
        </div>

        {/* Total Sales Amount */}
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/30 p-3 shadow-sm dark:border-emerald-900/20 dark:bg-emerald-900/5 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 sm:text-[11px]">
            Total Sales Amount
          </p>
          <p className="mt-1 text-base font-black text-emerald-600 sm:mt-2 sm:text-xl text-right font-mono">
            {formatCurrency(totals.totalSalesAmount)}
          </p>
        </div>

        {/* Total Grand Total */}
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/30 p-3 shadow-sm dark:border-emerald-900/20 dark:bg-emerald-900/5 sm:p-4">
          <p className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 sm:text-[11px]">
            Total Grand Total
          </p>
          <p className="mt-1 text-base font-black text-emerald-600 sm:mt-2 sm:text-xl text-right font-mono">
            {formatCurrency(totals.totalGrandTotal)}
          </p>
        </div>

        {/* Yard Name */}
        {selectedLotId && lotReportData[0] && (
          <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:text-[11px]">
              Yard Name
            </p>
            <p className="mt-1 text-base font-black text-slate-950 dark:text-white sm:mt-2 sm:text-xl truncate">
              {lotReportData[0].yardNo || "N/A"}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* BL Section */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-100 bg-white px-5 py-2 dark:border-slate-800 dark:bg-slate-900">
            <button
              onClick={() => toggleSection("bl")}
              className="flex items-center gap-2 transition-opacity hover:opacity-70"
            >
              <svg
                className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${expandedSections.bl ? "rotate-180" : ""}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                BL Details
              </h2>
            </button>
          </div>
          {expandedSections.bl && (
            <DataTable
              columns={blColumns}
              data={reportData.blRows}
              isLoading={isLoading}
              searchable={false}
              paginated={false}
              scrollHeight="30vh"
            />
          )}
        </div>

        {/* Purchase Section */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-100 bg-white px-5 py-2 dark:border-slate-800 dark:bg-slate-900">
            <button
              onClick={() => toggleSection("purchase")}
              className="flex items-center gap-2 transition-opacity hover:opacity-70"
            >
              <svg
                className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${expandedSections.purchase ? "rotate-180" : ""}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Purchase Details
              </h2>
            </button>
          </div>
          {expandedSections.purchase && (
            <DataTable
              columns={purchaseColumns}
              data={reportData.purchaseRows}
              isLoading={isLoading}
              searchable={false}
              paginated={false}
              scrollHeight="30vh"
            />
          )}
        </div>

        {/* Available Stock Section */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-100 bg-white px-5 py-2 dark:border-slate-800 dark:bg-slate-900">
            <button
              onClick={() => toggleSection("lotStock")}
              className="flex items-center gap-2 transition-opacity hover:opacity-70"
            >
              <svg
                className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${expandedSections.lotStock ? "rotate-180" : ""}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Available Stock in Lot
              </h2>
            </button>
          </div>
          {expandedSections.lotStock && (
            <DataTable
              columns={lotStockColumns}
              data={reportData.lotStockRows}
              isLoading={isLoading}
              searchable={false}
              paginated={false}
              scrollHeight="30vh"
            />
          )}
        </div>

        {selectedLotId && (
          <>
            {/* Stock Section */}
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
              <div className="flex items-center justify-between border-b border-slate-100 bg-white px-5 py-2 dark:border-slate-800 dark:bg-slate-900">
                <button
                  onClick={() => toggleSection("stock")}
                  className="flex items-center gap-2 transition-opacity hover:opacity-70"
                >
                  <svg
                    className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${expandedSections.stock ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    Stock Details
                  </h2>
                </button>
              </div>
              {expandedSections.stock && (
                <DataTable
                  columns={stockColumns}
                  data={reportData.stockDetails}
                  isLoading={isLoading}
                  searchable={false}
                  paginated={false}
                  scrollHeight="30vh"
                />
              )}
            </div>

            {/* Processing Section */}
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
              <div className="flex items-center justify-between border-b border-slate-100 bg-white px-5 py-2 dark:border-slate-800 dark:bg-slate-900">
                <button
                  onClick={() => toggleSection("processing")}
                  className="flex items-center gap-2 transition-opacity hover:opacity-70"
                >
                  <svg
                    className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${expandedSections.processing ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    Stock Processing
                  </h2>
                </button>
              </div>
              {expandedSections.processing && (
                <DataTable
                  columns={historyColumns}
                  data={reportData.processingHistory}
                  isLoading={isLoading}
                  searchable={false}
                  paginated={false}
                  scrollHeight="30vh"
                />
              )}
            </div>

            {/* Stock Transfer Details Section */}
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
              <div className="flex items-center justify-between border-b border-slate-100 bg-white px-5 py-2 dark:border-slate-800 dark:bg-slate-900">
                <button
                  onClick={() => toggleSection("transfer")}
                  className="flex items-center gap-2 transition-opacity hover:opacity-70"
                >
                  <svg
                    className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${expandedSections.transfer ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    Stock Transfer Details
                  </h2>
                </button>
              </div>
              {expandedSections.transfer && (
                <DataTable
                  columns={transferColumns}
                  data={reportData.transferRows}
                  isLoading={isLoading}
                  searchable={false}
                  paginated={false}
                  scrollHeight="30vh"
                />
              )}
            </div>

            {/* Selling Section */}
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
              <div className="flex items-center justify-between border-b border-slate-100 bg-white px-5 py-2 dark:border-slate-800 dark:bg-slate-900">
                <button
                  onClick={() => toggleSection("selling")}
                  className="flex items-center gap-2 transition-opacity hover:opacity-70"
                >
                  <svg
                    className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${expandedSections.selling ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    Selling Details
                  </h2>
                </button>
              </div>
              {expandedSections.selling && (
                <DataTable
                  columns={sellingColumns}
                  data={reportData.sellingRows}
                  isLoading={isLoading}
                  searchable={false}
                  paginated={false}
                  scrollHeight="30vh"
                />
              )}
            </div>

            {/* Expense Section */}
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
              <div className="flex items-center justify-between border-b border-slate-100 bg-white px-5 py-2 dark:border-slate-800 dark:bg-slate-900">
                <button
                  onClick={() => toggleSection("expense")}
                  className="flex items-center gap-2 transition-opacity hover:opacity-70"
                >
                  <svg
                    className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${expandedSections.expense ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    Expense Details
                  </h2>
                </button>
              </div>
              {expandedSections.expense && (
                <DataTable
                  columns={expenseColumns}
                  data={reportData.expenseRows}
                  isLoading={isLoading}
                  searchable={false}
                  paginated={false}
                  scrollHeight="30vh"
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LotWiseReportPage;
